import { z } from '../zod.js';
import {
  CursorPaginationValidationBaseSchema,
  withCursorPaginationValidation,
} from '../shared/cursorPagination';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { LivemodeFilterValidationSchema } from '../shared/livemode';

export const SUBSCRIPTION_PAYMENT_PAYMENT_STATUSES = [
  'succeeded',
  'pending',
  'failed',
  'incomplete',
] as const;

export const SUBSCRIPTION_PAYMENT_INVOICE_STATUSES = [
  'draft',
  'open',
  'paid',
  'uncollectible',
  'void',
] as const;

export const SUBSCRIPTION_PAYMENT_BILLING_REASONS = [
  'manual',
  'subscription_create',
  'subscription_cycle',
  'subscription_threshold',
  'subscription_update',
  'upcoming',
] as const;

export const SubscriptionPaymentListQueryValidationSchema = withCursorPaginationValidation(
  CursorPaginationValidationBaseSchema.extend({
    subscriptionId: ObjectIdStringValidationSchema.optional().meta({
      description:
        'Filter by parent subscription ID. Use to retrieve the full per-cycle payment history for one subscription (e.g. "show me payments for subscription X" or "when was subscription X last charged?"). Results are newest-first by default, so the first record is the most recent payment.',
    }),
    customerId: ObjectIdStringValidationSchema.optional().meta({
      description: 'Filter subscription payments by customer ID.',
    }),
    pageId: ObjectIdStringValidationSchema.optional().meta({
      description: 'Filter subscription payments by page ID.',
    }),
    paymentStatus: z
      .enum(SUBSCRIPTION_PAYMENT_PAYMENT_STATUSES)
      .optional()
      .meta({
        description: `Filter by status of the underlying Stripe charge or payment intent. Valid values: ${SUBSCRIPTION_PAYMENT_PAYMENT_STATUSES.join(', ')}. Use \`failed\` to find failed attempts (e.g. "show me failed subscription payments"). Note: a record can have \`paymentStatus: 'failed'\` while the invoice is still \`open\` — if a later retry succeeded, both \`paymentStatus\` and \`paid\` reflect success. For overall invoice lifecycle use the \`invoiceStatus\` field on the response; for a simple "was this paid?" check use \`paid\`.`,
      }),
    paid: z.boolean().optional().meta({
      description:
        'Filter by whether the invoice was fully collected. `true` = invoice paid in full, `false` = unpaid (use for "show me unpaid invoices"). Convenience field — an invoice can be `paid: true` even after a failed attempt followed by a successful retry.',
    }),
    refunded: z.boolean().optional().meta({
      description:
        'Filter by whether the payment was refunded. Combine with `createdAfter` to scope to a time window (e.g. "show me refunded subscription payments this month").',
    }),
    billingReason: z
      .enum(SUBSCRIPTION_PAYMENT_BILLING_REASONS)
      .optional()
      .meta({
        description: `Filter by reason the invoice was generated. Valid values: ${SUBSCRIPTION_PAYMENT_BILLING_REASONS.join(', ')}.
\`subscription_create\`: first payment when the subscription is created and activated; the customer's initial charge.
\`subscription_cycle\`: standard renewal at the end of a billing period.
\`subscription_update\`: charge from a plan change or proration; can explain an unexpected charge after switching plans.
\`subscription_threshold\`: metered billing usage threshold trigger.
\`manual\`: manually triggered by the merchant.
\`upcoming\`: preview of an upcoming invoice; not yet charged.`,
      }),
    createdAfter: z.iso.datetime().optional().meta({
      description:
        'Filter by lower bound of `createdAt`. Use a UTC ISO 8601 timestamp with the `Z` suffix (e.g. `2026-03-01T00:00:00Z`). Never use local timezone offsets — dates are stored and compared in UTC.',
    }),
    createdBefore: z.iso.datetime().optional().meta({
      description:
        'Filter by upper bound of `createdAt`. Use a UTC ISO 8601 timestamp with the `Z` suffix (e.g. `2026-03-31T23:59:59Z`). Never use local timezone offsets — dates are stored and compared in UTC.',
    }),
    livemode: LivemodeFilterValidationSchema,
  }).refine(
    data =>
      !(
        data.createdAfter &&
        data.createdBefore &&
        Date.parse(data.createdAfter) > Date.parse(data.createdBefore)
      ),
    {
      message: 'createdAfter must be earlier than or equal to createdBefore',
      path: ['createdBefore'],
    },
  ),
);
